"use client";
import * as React from "react";
import { useNavigate } from "react-router-dom";

function CTASection() {
  const navigate = useNavigate();

  const handleContactClick = () => {
    navigate("/contact");
    window.scrollTo(0, 0);
  };

  return (
    <section
      className="relative py-24 overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #1719A8 0%, #0F1280 100%)",
      }}
      role="banner"
      aria-labelledby="cta-heading"
    >
      {/* Top Wave */}
      <div className="absolute top-0 left-0 right-0" aria-hidden="true">
        <svg viewBox="0 0 1200 80" className="w-full h-auto" fill="#ffffff">
          <path
            d="M0,40 C300,0 500,80 800,40 C1000,10 1100,60 1200,40 L1200,0 L0,0 Z"
            style={{
              fill: "linear-gradient(135deg, #1719A8 0%, #0F1280 100%)",
            }}
          />
        </svg>
      </div>

      {/* Glowing Background Blobs */}
      <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
        <div
          className="absolute top-20 left-10 w-96 h-96 rounded-full opacity-20 blur-3xl animate-pulse"
          style={{
            background: "radial-gradient(circle, #D96F2B 0%, transparent 70%)",
          }}
        ></div>
        <div
          className="absolute bottom-20 right-10 w-80 h-80 rounded-full opacity-15 blur-2xl"
          style={{
            background: "radial-gradient(circle, #C9C51A 0%, transparent 70%)",
          }}
        ></div>
        <div
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full opacity-10 blur-xl"
          style={{
            background: "radial-gradient(circle, #E8ECFF 0%, transparent 70%)",
          }}
        ></div>
      </div>

      {/* Floating Transparent Circles */}
      <div className="absolute inset-0" aria-hidden="true">
        <div className="absolute top-32 right-1/4 w-24 h-24 bg-white/5 rounded-full backdrop-blur-sm animate-float"></div>
        <div className="absolute bottom-1/3 left-1/4 w-16 h-16 bg-white/10 rounded-full backdrop-blur-sm animate-float-delayed"></div>
        <div className="absolute top-2/3 right-1/3 w-32 h-32 bg-white/5 rounded-full backdrop-blur-sm animate-float"></div>
      </div>

      {/* Layered Liquid Shapes */}
      <div className="absolute inset-0" aria-hidden="true">
        <svg viewBox="0 0 1200 800" className="w-full h-full opacity-10">
          <defs>
            <linearGradient
              id="liquidGradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop offset="0%" stopColor="#D96F2B" stopOpacity="0.3" />
              <stop offset="50%" stopColor="#C9C51A" stopOpacity="0.2" />
              <stop offset="100%" stopColor="#E8ECFF" stopOpacity="0.1" />
            </linearGradient>
          </defs>
          <path
            d="M0,200 C300,100 500,300 800,200 C1000,150 1100,250 1200,200 L1200,600 C900,500 700,700 400,600 C200,550 100,650 0,600 Z"
            fill="url(#liquidGradient)"
          />
        </svg>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <div className="space-y-3 pb-10">
          <div className="inline-flex items-center px-6 py-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-full">
            <span
              className="w-2 h-2 bg-[#C9C51A] rounded-full mr-3 animate-pulse"
              aria-hidden="true"
            ></span>
            <span className="text-white/90 text-sm font-bold uppercase tracking-wider">
              Ready to Transform?
            </span>
          </div>

          <h1
            id="cta-heading"
            className="text-5xl lg:text-6xl font-black leading-normal text-white"
          >
            Start Your
            <span
              className="block bg-gradient-to-r bg-clip-text text-transparent pb-3"
              style={{
                backgroundImage:
                  "linear-gradient(135deg, #E8ECFF 0%, #D96F2B 50%, #C9C51A 100%)",
              }}
            >
              Digital Revolution
            </span>
            Today
          </h1>

          <p className="text-lg text-white/80 leading-relaxed max-w-2xl mx-auto">
            Join thousands of enterprises who have already transformed their
            business with our AI-powered platform. Experience the future of
            enterprise technology.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center pt-8">
            <button
              onClick={handleContactClick}
              className="group px-10 py-5 bg-white text-[#1719A8] font-bold text-lg rounded-full transition-all duration-300 shadow-2xl hover:shadow-white/25 hover:scale-105 focus:outline-none focus:ring-4 focus:ring-white/50 focus:scale-105 flex items-center justify-center"
              aria-label="Contact us to start your digital transformation"
            >
              Contact Us
              <svg
                className="ml-3 w-6 h-6 group-hover:translate-x-1 transition-transform duration-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0" aria-hidden="true">
        <svg viewBox="0 0 1200 120" className="w-full h-auto">
          <path
            d="M0,60 C300,120 500,0 800,60 C1000,100 1100,40 1200,60 L1200,120 L0,120 Z"
            fill="#0B1026"
          />
        </svg>
      </div>

      {/* Custom Animations */}
      <style >{`
        @keyframes float {
          0%,
          100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-25px) rotate(5deg);
          }
        }
        @keyframes float-delayed {
          0%,
          100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-20px) rotate(-3deg);
          }
        }
        .animate-float {
          animation: float 10s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 12s ease-in-out infinite 4s;
        }
      `}</style>
    </section>
  );
}

export default CTASection;
